import { useState } from "react";
import { toast } from "sonner";
import { Briefcase } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export function ClientSwitcher() {
  const { session } = useAuth();
  const clients = session.clients || [];
  if (session.user.role !== "consultant" || !session.org) return null;
  const change = async (e) => {
    try {
      await api.post("/consultant/switch", { org_id: e.target.value });
      window.location.href = "/app";
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="px-5 pb-4" data-testid="client-switcher">
      <p className="text-[10px] uppercase tracking-[0.16em] text-sky-300 font-semibold flex items-center gap-1.5"><Briefcase className="w-3 h-3" />Consultant · client</p>
      <select value={session.org.id} onChange={change} className="mt-1.5 w-full bg-white/10 text-white text-sm rounded-md px-2 py-1.5 border border-white/10" data-testid="client-switcher-select">
        {clients.map((c) => <option key={c.org_id} value={c.org_id} className="text-slate-900">{c.name}</option>)}
      </select>
    </div>
  );
}

export function NoClients() {
  const { session, logout } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="bg-white border border-slate-200 rounded-xl p-8 max-w-md text-center" data-testid="no-clients">
        <Briefcase className="w-8 h-8 text-sky-600 mx-auto" />
        <h1 className="mt-4 text-xl font-bold">No client organisations yet</h1>
        <p className="mt-2 text-sm text-slate-600">Hi {session.user.name}. Your consultant login is ready. When a client authorises you from their Users &amp; Licences page, they will appear here and you can switch between them.</p>
        <button onClick={logout} className="mt-6 text-sm font-semibold text-sky-700" data-testid="no-clients-logout">Log out</button>
      </div>
    </div>
  );
}

export function ConsultantsCard({ org, onChange }) {
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ name: "", email: "" });
  const load = () => api.get("/org/consultants").then((r) => setRows(r.data)).catch(() => setRows([]));
  if (rows === null) {
    load();
    return null;
  }
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      setF({ name: "", email: "" });
      load();
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="consultants-card">
      <h2 className="font-semibold text-lg">Consultants</h2>
      <p className="mt-1 text-sm text-slate-600">Give your quality consultant or auditor secure access to your management system. They can create and edit registers, audits, NCRs and documents, but cannot change billing or users. Each consultant uses one user licence, and you can remove access at any time. All their activity is recorded in your audit log.</p>
      <form onSubmit={(e) => { e.preventDefault(); act(() => api.post("/org/consultants", f), "Consultant invited"); }} className="mt-4 grid sm:grid-cols-[1fr_1fr_auto] gap-2">
        <input required minLength={2} placeholder="Consultant name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="field" data-testid="consultant-name-input" />
        <input required type="email" placeholder="Consultant email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="field" data-testid="consultant-email-input" />
        <button disabled={org && org.seats_available <= 0} className="bg-sky-600 disabled:bg-slate-300 text-white font-semibold px-4 py-2 rounded-md text-sm" data-testid="consultant-invite-btn">Authorise consultant</button>
      </form>
      {org && org.seats_available <= 0 && <p className="mt-2 text-xs text-amber-700">No licences available. Purchase an additional user to add a consultant.</p>}
      <div className="mt-4 divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`consultant-row-${r.email}`}>
            <span><b>{r.name}</b> · {r.email} · <span className={r.accepted ? "text-emerald-700" : "text-amber-700"}>{r.accepted ? "Active" : "Invited"}</span></span>
            <button onClick={() => window.confirm(`Remove ${r.name}'s access?`) && act(() => api.delete(`/org/consultants/${r.id}`), "Consultant access removed")} className="text-red-700 font-semibold" data-testid={`consultant-revoke-${r.email}`}>Remove access</button>
          </div>
        ))}
        {!rows.length && <p className="py-2 text-sm text-slate-500">No consultants authorised.</p>}
      </div>
    </div>
  );
}
